/**
 * Iter XX.77.1 — Founder Review View for R3-lite sessions.
 *
 * Admin-only surface. Lists real R3-lite onboarding sessions so the
 * founder can review the actual first turn (context + coaching
 * question + chat history) and classify each session Green / Yellow
 * / Red before we roll out to 5% of traffic.
 *
 * Endpoints used:
 *   GET  /api/admin/onboarding-review/stats
 *   GET  /api/admin/onboarding-review/sessions?classification=…&limit=50
 *   POST /api/admin/onboarding-review/classify
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";

const BASE = "/admin/onboarding-review";

const CLASSIFICATIONS = [
  { id: "green",  label: "Green",  color: "bg-emerald-500/20 text-emerald-300 border-emerald-700" },
  { id: "yellow", label: "Yellow", color: "bg-amber-500/20 text-amber-300 border-amber-700" },
  { id: "red",    label: "Red",    color: "bg-rose-500/20 text-rose-300 border-rose-700" },
];

const FILTERS = [
  { id: "unclassified", label: "Needs review" },
  { id: "green",        label: "Green" },
  { id: "yellow",       label: "Yellow" },
  { id: "red",          label: "Red" },
  { id: "all",          label: "All" },
];

function StatPill({ label, value }) {
  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-950/60 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
      <div className="text-lg font-medium text-neutral-100">{value ?? "—"}</div>
    </div>
  );
}

function Message({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div
      data-testid={`onboarding-review-msg-${msg.role}`}
      className={
        "text-[13px] leading-relaxed whitespace-pre-wrap rounded-md px-3 py-2 " +
        (isUser
          ? "bg-neutral-900/60 border border-neutral-800 text-neutral-100"
          : "bg-emerald-950/25 border border-emerald-900/60 text-emerald-100")
      }
    >
      <div className="text-[10px] uppercase tracking-wider mb-1 text-neutral-500">
        {msg.role}
      </div>
      {msg.content}
    </div>
  );
}

function ContextBlock({ ctx }) {
  if (!ctx) return null;
  const rows = [
    ["Surface", ctx.surface],
    ["Recent experience", ctx.recent_experience],
    ["Diagnostic answer", ctx.diagnostic_answer],
    ["Used escape", ctx.used_escape ? "yes" : "no"],
    ["Reflection shown", ctx.reflection_shown],
    ["Coaching question", ctx.coaching_question],
    ["Copy version", ctx.copy_version],
    ["Completed at", ctx.completed_at],
  ];
  return (
    <div className="text-[12px] grid grid-cols-2 gap-x-4 gap-y-1 text-neutral-300">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <div className="text-neutral-500">{k}</div>
          <div className="text-neutral-100 truncate" title={String(v ?? "")}>
            {v == null || v === "" ? "—" : String(v)}
          </div>
        </div>
      ))}
    </div>
  );
}

function ClassifierRow({ session, onClassify, submitting }) {
  const [notes, setNotes] = useState(session?.r3_review?.notes || "");
  const current = session?.r3_review?.classification || null;
  return (
    <div className="space-y-2">
      <input
        data-testid="onboarding-review-notes-input"
        value={notes}
        onChange={(e) => setNotes(e.target.value.slice(0, 2000))}
        placeholder="Notes (optional) — why this is Green/Yellow/Red…"
        className="w-full text-[13px] px-3 py-2 rounded-md bg-neutral-950 border border-neutral-800 text-neutral-100 placeholder:text-neutral-600 focus:outline-none focus:border-emerald-700"
      />
      <div className="flex flex-wrap items-center gap-2">
        {CLASSIFICATIONS.map((c) => (
          <button
            key={c.id}
            disabled={submitting}
            data-testid={`onboarding-review-classify-${c.id}`}
            onClick={() => onClassify(c.id, notes)}
            className={
              "text-[12px] px-3 py-1.5 rounded-full border transition-colors " +
              (current === c.id
                ? c.color + " font-medium"
                : "border-neutral-800 text-neutral-300 hover:border-neutral-600 hover:text-neutral-100") +
              " disabled:opacity-40"
            }
          >
            {c.label}
          </button>
        ))}
        {current ? (
          <span className="text-[11px] text-neutral-500 ml-2">
            Last: <b>{current}</b>{session.r3_review.classified_by ? ` · ${session.r3_review.classified_by}` : ""}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function SessionCard({ session, onClassify, submitting }) {
  const ctx = session.r3_context || {};
  return (
    <div
      data-testid={`onboarding-review-session-${session.session_id}`}
      className="border border-neutral-800 rounded-xl bg-neutral-950/40 p-4 space-y-3"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="text-[12px] text-neutral-500 font-mono">
          {session.session_id}
        </div>
        <div className="text-[11px] text-neutral-500">
          {session.created_at || ctx.updated_at || ""}
        </div>
      </div>
      <ContextBlock ctx={ctx} />
      <div className="space-y-1.5">
        {(session.messages || []).map((m, i) => (
          <Message key={i} msg={m} />
        ))}
        {(!session.messages || session.messages.length === 0) && (
          <div className="text-[12px] text-neutral-500 italic">
            No chat messages yet — R3 completed but Step 4 not sent.
          </div>
        )}
      </div>
      <ClassifierRow
        session={session}
        submitting={submitting}
        onClassify={(label, notes) => onClassify(session.session_id, label, notes)}
      />
    </div>
  );
}

export default function AdminOnboardingReview() {
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [filter, setFilter] = useState("unclassified");
  const [limit, setLimit] = useState(50);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get(`${BASE}/stats`);
      setStats(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load stats.");
    }
  }, []);

  const loadSessions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit };
      if (filter !== "all") params.classification = filter;
      const { data } = await api.get(`${BASE}/sessions`, { params });
      setSessions(data?.sessions || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load sessions.");
    } finally {
      setLoading(false);
    }
  }, [filter, limit]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadSessions(); }, [loadSessions]);

  async function handleClassify(sessionId, label, notes) {
    setSubmitting(true);
    try {
      await api.post(`${BASE}/classify`, {
        session_id: sessionId, classification: label, notes: notes || null,
      });
      toast.success(`Marked ${sessionId.slice(-6)} as ${label}`);
      // Optimistic patch — no full re-fetch so the founder can keep flowing.
      setSessions((prev) =>
        prev.map((s) =>
          s.session_id === sessionId
            ? { ...s, r3_review: { classification: label, notes,
                classified_at: new Date().toISOString() } }
            : s,
        ),
      );
      loadStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Classify failed");
    } finally {
      setSubmitting(false);
    }
  }

  const versionPills = useMemo(() => {
    const by = stats?.by_copy_version || {};
    return Object.keys(by).sort().map((k) => ({ id: k, count: by[k] }));
  }, [stats]);

  return (
    <div
      data-testid="onboarding-review-page"
      className="min-h-screen bg-neutral-950 text-neutral-100 px-4 py-6 md:px-10 md:py-10"
    >
      <div className="max-w-5xl mx-auto space-y-6">
        <header className="space-y-2">
          <h1 className="text-2xl font-medium tracking-tight">
            R3-lite · Founder Review
          </h1>
          <p className="text-[13px] text-neutral-400">
            Inspect the first real R3-lite sessions. Classify each as
            <span className="text-emerald-400"> Green</span> ·
            <span className="text-amber-400"> Yellow</span> ·
            <span className="text-rose-400"> Red</span> before the 5% rollout.
          </p>
        </header>

        <section className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <StatPill label="Total" value={stats?.total} />
          <StatPill label="Needs review" value={stats?.unclassified} />
          <StatPill label="Green" value={stats?.green} />
          <StatPill label="Yellow" value={stats?.yellow} />
          <StatPill label="Red" value={stats?.red} />
        </section>

        {versionPills.length > 0 && (
          <section
            data-testid="onboarding-review-version-pills"
            className="flex flex-wrap gap-2 text-[11px]"
          >
            <span className="text-neutral-500 pt-1">Copy versions:</span>
            {versionPills.map((v) => (
              <span
                key={v.id}
                className="px-2 py-1 rounded-full border border-neutral-800 text-neutral-300"
              >
                {v.id} · {v.count}
              </span>
            ))}
          </section>
        )}

        <section className="flex flex-wrap items-center gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              data-testid={`onboarding-review-filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={
                "text-[12px] px-3 py-1.5 rounded-full border transition-colors " +
                (filter === f.id
                  ? "border-emerald-600 bg-emerald-950/40 text-emerald-200"
                  : "border-neutral-800 text-neutral-400 hover:border-neutral-600 hover:text-neutral-100")
              }
            >
              {f.label}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2 text-[11px] text-neutral-500">
            <label htmlFor="review-limit">Limit</label>
            <select
              id="review-limit"
              value={limit}
              onChange={(e) => setLimit(parseInt(e.target.value, 10))}
              className="bg-neutral-950 border border-neutral-800 rounded px-2 py-1 text-neutral-200"
            >
              {[25, 50, 100, 200].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <button
              data-testid="onboarding-review-refresh"
              onClick={() => { loadStats(); loadSessions(); }}
              className="px-3 py-1 rounded border border-neutral-800 hover:border-neutral-600 text-neutral-300"
            >
              Refresh
            </button>
          </div>
        </section>

        {error && (
          <div className="text-[13px] text-rose-300 border border-rose-900/60 bg-rose-950/40 rounded-md px-3 py-2">
            {error}
          </div>
        )}

        <section className="space-y-4">
          {loading ? (
            <div className="text-neutral-500 text-[13px]">Loading…</div>
          ) : sessions.length === 0 ? (
            <div className="text-neutral-500 text-[13px]">
              No sessions match this filter.
            </div>
          ) : (
            sessions.map((s) => (
              <SessionCard
                key={s.session_id}
                session={s}
                submitting={submitting}
                onClassify={handleClassify}
              />
            ))
          )}
        </section>
      </div>
    </div>
  );
}
